import React, { useEffect, useState, useCallback } from "react";
import {
  OpenTournamentBtn,
  BeginTournamentYearBtn,
  CloseTournamentBtn,
  StartBattleFinalistWeekBtn,
  StartTieBreakFinalistWeekBtn,
  StartChampionshipBattleRoundBtn,
  StartTieBreakChampionshipRoundBtn,
  DecideFinalistWinnerWeekBtn,
  DecideChampionWinnerWeekBtn,
  DecideFinalistTieBreakerWeekBtn,
  DecideChampionTieBreakerWeekBtn,
  PurgeContestantsPrepNextFinalistRoundBtn,
  PurgeFinalistsPrepChampionshipRoundBtn,
  PurgeChampionshipContestantsPrepBeginTournamentYrBtn,
  NextSeasonApproval,
  SetWarnClosesTodayBtn,
  SetWarnEndsTodayBtn,
  CreateNextChampionshipTournamentRoundBtn,
  CreateNextFinalsTournamentRoundBtn,
} from "../../components/buttons";
import Loader from "../../components/loader/loader";
import { computeTournamentButtonState } from "../../controller/tournamentControllerRules";
import NeumorphismField from "../../components/neumorphismField/neumorphismField";
import MockTournamentData from "../../constants/mockTournamentData";
import {
  apiPurgeLastFinalistRoundChampionShipPrep,
  apiGetCurrentTournamentRound,
  apiCreateNextFinalsTournamentRoundOnThisRecord,
  apiBeginTournamentYear,
  apiUpdateTournamentRound,
  apiDecideRound,
  apiApproveNextSeason,
  apiCreateNextChampionshipRoundOnThisRecord,
} from "../../api/backend/tournament";
import "./tournamentControlPanelPage.scss";

const TournamentControlPanelPage = () => {
  const [seasonMaxRounds, setSeasonMaxRounds] = useState("");
  const [seasonMaxRoundsError, setSeasonMaxRoundsError] = useState("");
  const [tournamentRound, setTournamentRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // ------------------------------------------------------------
  // Load current round from backend
  // ------------------------------------------------------------
  const refreshRound = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGetCurrentTournamentRound();
      setTournamentRound(res?.data?.round ?? null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRound();
  }, [refreshRound]);

  const runCommand = useCallback(
    async (commandFn) => {
      if (busy) return;
      try {
        setBusy(true);
        setError("");

        const maybeRound = await commandFn(); // 👈 command returns updated round
        if (maybeRound) setTournamentRound(maybeRound);
        else await refreshRound(); // fallback
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.message || "Command failed.");
      } finally {
        setBusy(false);
      }
    },
    [busy, refreshRound]
  );

  // Small helper so every button doesn’t repeat try/catch boilerplate
  const patchRoundRaw = useCallback(
    async (patch) => {
      if (!tournamentRound?.id) return null;
      const res = await apiUpdateTournamentRound(tournamentRound.id, patch);
      return res?.data?.round ?? res?.data ?? null;
    },
    [tournamentRound?.id]
  );
  // ------------------------------------------------------------
  // Enable / disable rules
  // ------------------------------------------------------------
  // ✅ compute allowed flags + key
  const { allowed, key } = computeTournamentButtonState(tournamentRound ?? {});
  const onSetWarnClosesToday = () => {
    if (!allowed.canSetWarnClosesToday || busy) return;
    return runCommand(() => patchRoundRaw({ period: "closesToday" }));
  };
  // ------------------------------------------------------------
  // Button handlers (backend calls)
  // ------------------------------------------------------------

  const onNextSeasonApproval = () => {
    if (!allowed.nextSeasonApprovalNeeded || busy) return;
    return runCommand(async () => {
      const res = await apiApproveNextSeason({
        stage: "kickoffSeason",
        status: "closed",
        period: "opensToday",
        tournamentRoundCount: 0,
      });
      return res?.data?.round ?? null;
    });
  };

  const handleSeasonMaxRoundsChange = (nextValue) => {
    // always store raw string so typing feels normal
    // but we can still react to invalid values
    if (seasonMaxRoundsError) setSeasonMaxRoundsError("");

    // If they type 0 (or 00, 000) treat as invalid:
    if (nextValue !== "" && Number(nextValue) === 0) {
      setSeasonMaxRoundsError("Must be greater than 0");
      setSeasonMaxRounds(""); // 👈 key move: clear so placeholder can show
      return;
    }

    // If they type a negative, same deal:
    if (nextValue !== "" && Number(nextValue) < 0) {
      setSeasonMaxRoundsError("Must be greater than 0");
      setSeasonMaxRounds("");
      return;
    }

    setSeasonMaxRounds(nextValue);
  };

  const onBeginTournamentYear = () => {
    if (!allowed.canBeginTournamentYear || busy) return;
    const maxRounds = Number(seasonMaxRounds);
    if (!Number.isFinite(maxRounds) || maxRounds <= 0) {
      setSeasonMaxRoundsError("Must be greater than 0");
      setSeasonMaxRounds(""); // force placeholder error to show
      return;
    }
    return runCommand(async () => {
      const res = await apiBeginTournamentYear({
        seasonMaxRounds: maxRounds,
        stage: "finalist",
        status: "closed",
        period: "opensToday",
      });
      setSeasonMaxRounds("");
      return res?.data?.round ?? null;
    });
  };

  const onOpenFinalistContestants = () => {
    if (!allowed.canOpenFinalistContestants || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "open", period: "nowOpen" })
    );
  };

  const onCreateNextFinalsTournamentRound = () => {
    if (!allowed.canCreateNextFinalsTournamentRound || busy) return;

    return runCommand(async () => {
      const res = await apiCreateNextFinalsTournamentRoundOnThisRecord(
        tournamentRound.id,
        { status: "closed", period: "opensToday" }
      );
      return res?.data?.round ?? null;
    });
  };

  const onCreateNextChampionshipTournamentRound = () => {
    if (!allowed.canCreateNextChampionshipTournamentRound || busy) return;

    return runCommand(async () => {
      const res = await apiCreateNextChampionshipRoundOnThisRecord(
        tournamentRound.id,
        {
          status: "closed",
          period: "opensToday",
        }
      );
      return res?.data?.round ?? null; // ✅ updated round comes back
    });
  };

  const onPurgePrepNextFinalistRound = () => {
    if (!allowed.canPurgePrepNextFinalistRound || busy) return;
  
    return runCommand(async () => {
      // Step 0: commit UI intent
      await patchRoundRaw({ status: "purging" });
  
      try {
        const res = await apiPurgeLastFinalistRoundChampionShipPrep({
          advanceStage: false,
        });
        const data = res?.data;
  
        if (!data?.ok) throw new Error(data?.message || "Purge failed.");
  
        if (data?.tieInfo?.hasTie) {
          return { ...(tournamentRound || {}), tieInfo: data.tieInfo };
        }
  
        return null; // refreshRound() pulls purgeCompleted etc
      } catch (e) {
        // ✅ backend source of truth rollback
        const rollbackPatch =
          e?.response?.data?.rollbackPatch;
        await patchRoundRaw(rollbackPatch);
  
        throw e;
      }
    });
  };
  

  const onPurgeFinalistsAndAdvanceToChampionship = () => {
    if (!allowed.canPurgeLastFinalistRoundAdvanceChampionshipRound || busy) return;
    return runCommand(async () => {
      // Step 0: commit UI intent
      await patchRoundRaw({ status: "purging" });
      try {
        const res = await apiPurgeLastFinalistRoundChampionShipPrep({
          advanceStage: true,
        });
        const data = res?.data;
  
        if (!data?.ok) throw new Error(data?.message || "Purge/advance failed.");
  
        if (data?.tieInfo?.hasTie) {
          return { ...(tournamentRound || {}), tieInfo: data.tieInfo };
        }
        return null; // refreshRound() pulls purgeCompleted etc
      } catch (e) {
        // ✅ backend source of truth rollback
        const rollbackPatch = e?.response?.data?.rollbackPatch;
        await patchRoundRaw(rollbackPatch);
  
        throw e;
      }
    });
  };
  

  const onPurgeChampionshipContestantsPrepBeginTournamentYr = () => {
    if (!allowed.canPurgeChampionshipContestantsPrepBeginTournamentYr || busy)
      return;
    return runCommand(() => patchRoundRaw({ status: "purgeCompleted" }));
  };

  const onStartFinalistBattleRound = () => {
    if (!allowed.canStartFinalistBattleRound || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "competing", period: "broadcastBattling" })
    );
  };
  const onSetWarnEndsToday = () => {
    if (!allowed.canSetWarnEndsToday || busy) return;
    return runCommand(() => patchRoundRaw({ period: "endsToday" }));
  };

  const onStartTieBreakerFinalistRound = () => {
    if (!allowed.canStartTieBreakerFinalistRound || busy) return;
    return runCommand(() => patchRoundRaw({ period: "tieBreakerBattle" }));
  };
  const onStartChampionshipBattleRound = () => {
    if (!allowed.canStartChampionshipBattleRound || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "competing", period: "broadcastBattling" })
    );
  };
  const onStartTieBreakChampionshipRound = () => {
    if (!allowed.canStartTieBreakerChampionshipRound || busy) return;
    return runCommand(() => patchRoundRaw({ period: "tieBreakerBattle" }));
  };
  const onDecideFinalistWinner = () => {
    if (!allowed.canDecideFinalistWinner || busy) return;
    return runCommand(async () => {
      const res = await apiDecideRound(tournamentRound.id, {
        status: "deciding",
        period: "ended",
      });
      return res?.data?.round ?? null; // round already has tieInfo embedded
    });
  };

  const onDecideFinalistTieBreakerRound = () => {
    if (!allowed.canDecideTieBreakerFinalistRound || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "deciding", period: "ended" })
    );
  };

  const onDecideChampionWinner = () => {
    if (!allowed.canDecideChampionWinner || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "deciding", period: "ended" })
    );
  };

  const onDecideChampionTieBreakerRound = () => {
    if (!allowed.canDecideChampionTieBreakerRound || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "deciding", period: "ended" })
    );
  };
  const onCloseTournament = () => {
    if (!allowed.canCloseTournament || busy) return;
    return runCommand(() =>
      patchRoundRaw({ status: "closed", period: "nowClosed" })
    );
  };

  if (loading) {
    return (
      <div
        className="tournament-control-panel-page"
        id="topTournamentControlPanel"
      >
        <h2>Tournament Control Panel</h2>
        <Loader />
      </div>
    );
  }
  if (!tournamentRound) {
    return (
      <div
        className="tournament-control-panel-page"
        id="topTournamentControlPanel"
      >
        <h2>Tournament Control Panel</h2>
        <p>No tournament round found in DB.</p>
        <p style={{ opacity: 0.8 }}>
          Create/seed one kickoffSeason row, then refresh — buttons will unlock.
        </p>
        {error ? <p style={{ color: "tomato" }}>{error}</p> : null}
      </div>
    );
  }
  return (
    <div
      className="tournament-control-panel-page"
      id="topTournamentControlPanel"
    >
      <h2>Tournament Control Panel</h2>
      <div className="tcp-hud">
        <span className="tcp-chip tcp-chip-current">
          Current: stage {tournamentRound.stage}; status{" "}
          {tournamentRound.status}; period {tournamentRound.period}
        </span>

        <span className="tcp-chip">
          {" "}
          Seasonal finalist rounds remaining:{" "}
          {tournamentRound.finalistRoundsRemaining}
        </span>
      </div>

      {error ? <p style={{ color: "tomato" }}>{error}</p> : null}
      {busy ? <p style={{ opacity: 0.7 }}>Saving…</p> : null}
      <div className="tournment-ctrl-btns-wrapper">
        <div className="tournament-control-buttons">
          {allowed.canBeginTournamentYear && (
            <NeumorphismField //1
              className={`season-maxrounds-neumorphism-field ${
                seasonMaxRoundsError ? "tcp-error-border" : ""
              }`}
              label="Season max finalist rounds"
              type="number"
              value={seasonMaxRounds}
              onChange={(e) => handleSeasonMaxRoundsChange(e.target.value)}
              placeholder={seasonMaxRoundsError || "e.g. 8"}
            />
          )}
          <BeginTournamentYearBtn // start or next
            text="Begin Tournament Year"
            disabled={!allowed.canBeginTournamentYear || busy}
            onClick={onBeginTournamentYear}
            width="260px"
          />
        </div>
        <OpenTournamentBtn // next
          text="Open Finalist Contestants"
          disabled={!allowed.canOpenFinalistContestants || busy}
          onClick={onOpenFinalistContestants}
          width="260px"
        />
        <SetWarnClosesTodayBtn // next
          text="Set Closes Today"
          disabled={!allowed.canSetWarnClosesToday || busy}
          onClick={onSetWarnClosesToday}
          width="260px"
        />

        <CloseTournamentBtn // next
          text="Close Tournament"
          disabled={!allowed.canCloseTournament || busy}
          onClick={onCloseTournament}
          width="220px"
        />
        <StartBattleFinalistWeekBtn // next
          text={`Start Battle Finalist Round Contestants Round ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canStartFinalistBattleRound || busy}
          onClick={onStartFinalistBattleRound}
          width="420px"
        />
        <SetWarnEndsTodayBtn // next
          text="Set Ends Today"
          disabled={!allowed.canSetWarnEndsToday || busy}
          onClick={onSetWarnEndsToday}
          width="260px"
        />
        <DecideFinalistWinnerWeekBtn // next
          text={`Decide Finalist Winner Round ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canDecideFinalistWinner || busy}
          onClick={onDecideFinalistWinner}
          width="360px"
        />
        <StartTieBreakFinalistWeekBtn // if
          text={`Start Tie Break Finalist Round Contestants Round ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canStartTieBreakerFinalistRound || busy}
          onClick={onStartTieBreakerFinalistRound}
          width="460px"
        />
        {/* <SetWarnEndsTodayBtn so loop back up */}
        <DecideFinalistTieBreakerWeekBtn // then
          text={`Decide Finalist Tie Breaker ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canDecideTieBreakerFinalistRound || busy}
          onClick={onDecideFinalistTieBreakerRound}
          width="380px"
        />
        <PurgeContestantsPrepNextFinalistRoundBtn // else
          text="Purge Contestants (Prep Next Finalist Round)"
          disabled={!allowed.canPurgePrepNextFinalistRound || busy}
          onClick={onPurgePrepNextFinalistRound}
          width="320px"
        />
        <CreateNextFinalsTournamentRoundBtn //  next
          text="Next tournament round"
          disabled={!allowed.canCreateNextFinalsTournamentRound || busy}
          onClick={onCreateNextFinalsTournamentRound}
          width="260px"
        />
        <PurgeFinalistsPrepChampionshipRoundBtn // next
          text="Purge Finalists (Prep Championship Round)"
          disabled={
            !allowed.canPurgeLastFinalistRoundAdvanceChampionshipRound || busy
          }
          onClick={onPurgeFinalistsAndAdvanceToChampionship}
          width="320px"
        />
        <CreateNextChampionshipTournamentRoundBtn // next
          text="Next tournament round"
          disabled={!allowed.canCreateNextChampionshipTournamentRound || busy}
          onClick={onCreateNextChampionshipTournamentRound}
          width="260px"
        />
        {/* <OpenTournamentBtn  --> loop back up  */}
        {/* <CloseTournamentBtn  --> loop back up   */}
        <StartChampionshipBattleRoundBtn //next
          text={`Start Championship Battle Round ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canStartChampionshipBattleRound || busy}
          onClick={onStartChampionshipBattleRound}
          width="360px"
        />
        {/* <SetWarnEndsTodayBtn --> loop back up  */}
        <DecideChampionWinnerWeekBtn // next
          text={`Decide Champion Winner Round ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canDecideChampionWinner || busy}
          onClick={onDecideChampionWinner}
          width="380px"
        />
        <StartTieBreakChampionshipRoundBtn // if
          text={`Start Tie Break Championship Round - ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canStartTieBreakerChampionshipRound || busy}
          onClick={onStartTieBreakChampionshipRound}
          width="430px"
        />
        {/* <SetWarnEndsTodayBtn --> loop back up */}
        <DecideChampionTieBreakerWeekBtn // then
          text={`Decide Champion Tie Breaker ${tournamentRound.tournamentRoundCount}`}
          disabled={!allowed.canDecideChampionTieBreakerRound || busy}
          onClick={onDecideChampionTieBreakerRound}
          width="400px"
        />
      </div>
      <PurgeChampionshipContestantsPrepBeginTournamentYrBtn // next
        text="Purge Championship Contestants (Prep Begin Tournament Year)"
        disabled={
          !allowed.canPurgeChampionshipContestantsPrepBeginTournamentYr || busy
        }
        onClick={onPurgeChampionshipContestantsPrepBeginTournamentYr}
        width="360px"
      />
      <NextSeasonApproval // next and loop back up to next
        text="Approve Next Season"
        disabled={!allowed.nextSeasonApprovalNeeded || busy}
        onClick={onNextSeasonApproval}
        width="260px"
      />
      <pre className="debug">
        {JSON.stringify({ key, tournamentRound }, null, 2)}
      </pre>
    </div>
  );
};
export default TournamentControlPanelPage;

// const onDecideFinalistWinner = () => {
//   if (!canDecideFinalistWinner) return;
//   setMockDb((prevDb) => {
//     const currentId =
//       prevDb.currentTournamentRoundId ?? prevDb.tournamentRounds?.[0]?.id;
//     const hasTie = prevDb.roundEntries.some(
//       (re) => re.roundId === currentId && re.competitionStatus === "tied"
//     );
//     return onSuccessUpdateCurrentRound(prevDb, {
//       status: hasTie ? "tieBreaker" : "decisionComplete",
//      period: hasTie ? "tieBreakerBattle" : "ended",
//     });
//   });
// };
// const onDecideChampionWinner = () => {
//   if (!canDecideChampionWinner) return;
//   setMockDb((prevDb) => {
//     const currentId =
//       prevDb.currentTournamentRoundId ?? prevDb.tournamentRounds?.[0]?.id;
//     const hasTie = prevDb.roundEntries.some(
//       (re) => re.roundId === currentId && re.competitionStatus === "tied"
//     );
//     return onSuccessUpdateCurrentRound(prevDb, {
//       status: hasTie ? "tieBreaker" : "decisionComplete",
//      period: hasTie ? "tieBreakerBattle" : "ended",
//     });
//   });
// };

// const nextSeasonApprovalNeeded = // go through checklist to make sure the winning skit is retained and the others are deleted
//   tournamentRound.stage === "championship" &&
//   tournamentRound.status === "purgeCompleted" &&
//   tournamentRound.period === "ended";

// const canBeginTournamentYear =
//   tournamentRound.stage === "kickoffSeason" &&
//   tournamentRound.status === "closed" &&
//   tournamentRound.period === "opensToday";

// const canOpenFinalistContestants =
//   tournamentRound.stage === "finalist" &&
//   tournamentRound.status === "closed" &&
//   tournamentRound.period === "opensToday";

// const canCreateNextFinalsTournamentRound =
//   (tournamentRound.stage === "finalist" ||
//     tournamentRound.stage === "championship") &&
//   tournamentRound.status === "purgeCompleted";

// const canSetWarnClosesToday =
//   tournamentRound.status === "open" &&
//   !(
//     tournamentRound.period === "closesToday" ||
//     tournamentRound.period === "nowClosed"
//   );

// const canCloseTournament =
//   tournamentRound.status === "open" &&
//   tournamentRound.period === "closesToday";

// const canPurgePrepNextFinalistRound =
//   tournamentRound.stage === "finalist" && // purging all skits marked contestants for the round
//   tournamentRound.status === "decisionComplete" && // remember on the user table to mark them as championship contenter here as well.
//   tournamentRound.period === "ended" &&
//   tournamentRound.finalistRoundsRemaining > 0;

// const canPurgeLastFinalistRoundAdvanceChampionshipRound =
//   tournamentRound.stage === "finalist" && // looking on the usertable for all the ones who are marked as championship contender
//   tournamentRound.status === "decisionComplete" && // send them a notification on championship rules actviity log table
//   tournamentRound.period === "ended" && // purge all the skits and show the upload button to only championship contenders
//   tournamentRound.finalistRoundsRemaining === 0;

// const canPurgeChampionshipContestantsPrepBeginTournamentYr = // after this is successful will press the aprove next season button
//   tournamentRound.stage === "championship" && // skits icon winner is chosen and won the year
//   tournamentRound.status === "decisionComplete" &&
//   tournamentRound.period === "ended"; // so now keep the winning skit for the skit icons legends page and delete the losing skits

// (for now) enable the new buttons so you can wire rules later
// const canStartFinalistBattleRound =
//   isFinalist &&
//   tournamentRound.status === "closed" &&
//   tournamentRound.period === "nowClosed";

// const canSetWarnEndsToday =
//   (isFinalist || isChampionship) &&
//   (tournamentRound.status === "competing" ||
//     tournamentRound.status === "tieBreaker") &&
//   (tournamentRound.period === "broadcastBattling" ||
//     tournamentRound.period === "tieBreakerBattle");

// const canStartTieBreakerFinalistRound =
//   isFinalist &&
//   tournamentRound.status === "tieBreaker" &&
//   tournamentRound.period === "tieBreakerBattle";

// const canDecideFinalistWinner =
//   isFinalist &&
//   tournamentRound.status === "competing" &&
//   tournamentRound.period === "endsToday";

// const canDecideTieBreakerFinalistRound =
//   isFinalist &&
//   tournamentRound.status === "tieBreaker" &&
//   tournamentRound.period === "endsToday";

// const canStartChampionshipBattleRound =
//   isChampionship &&
//   tournamentRound.status === "closed" &&
//   tournamentRound.period === "nowClosed";

// const canStartTieBreakerChampionshipRound =
//   isChampionship &&
//   tournamentRound.status === "tieBreaker" &&
//   tournamentRound.period === "tieBreakerBattle";

// const canDecideChampionWinner =
//   isChampionship &&
//   tournamentRound.status === "competing" &&
//   tournamentRound.period === "endsToday";

// const canDecideChampionTieBreakerRound =
//   isChampionship &&
//   tournamentRound.status === "tieBreaker" &&
//   tournamentRound.period === "endsToday";
